import { Link, useParams } from "react-router-dom";
import { ArrowRight, CalendarBlank, Leaf, Sparkle, Sun } from "@phosphor-icons/react";
import { usePublicSite } from "@/context/PublicSiteContext";
import { renderMarkdown } from "@/lib/markdown";
import Seo from "@/components/Seo";
import { metaDescription, siteOgImage, siteUrl, jsonLdForOrg } from "@/lib/siteMeta";
import ShareButton from "@/components/marketing/ShareButton";

/**
 * Public homepage. Designed to feel warm and real even when the CMS is
 * sparsely populated: the hero is self-contained, the announcements
 * section hides cleanly if there are none, and the CTA is always present.
 */
export default function SiteHome() {
  const { orgSlug } = useParams();
  const { site } = usePublicSite();
  if (!site) return null;

  const { org, pages, announcements } = site;
  const base = `/site/${orgSlug}`;

  // Module E · the highlight blurb prefers an About-style page. We
  // explicitly exclude any page whose slug/title is 'programs' so the
  // new Programs section (rendered separately below) doesn't compete.
  const homeHighlight = (pages || []).find(
    (p) =>
      !/^programs?$/i.test(p.slug || "") &&
      !/^programs?$/i.test(p.title || "") &&
      /about|welcome|our center/i.test(p.title),
  );

  // Module E · if a `programs` page exists, parse its `## Heading`
  // sections into up to three calm cards on the home page.
  const programsPage = (pages || []).find(
    (p) => /^programs?$/i.test(p.slug || "") || /^programs?$/i.test(p.title || ""),
  );
  const programCards = programsPage ? parseProgramCards(programsPage.body_md, 3) : [];

  const visibleAnns = (announcements || []).slice(0, 3);

  const canonical = siteUrl(orgSlug);
  const siteName  = org.display_name || org.name;
  const title = `${siteName} · A warm daycare in your neighborhood`;
  const description = metaDescription(
    homeHighlight?.body_md ||
      `${siteName} is a small childcare center where every child is known by name. Calm classrooms, outdoor time, and daily reports for parents.`,
  );

  return (
    <div data-testid="public-home" className="space-y-10 md:space-y-14">
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        ogImage={siteOgImage(orgSlug, "home", siteName)}
        ogType="website"
        themeColor={org.brand_primary_hex}
        siteName={siteName}
        jsonLd={jsonLdForOrg(org, canonical)}
      />
      {/* Hero */}
      <section data-testid="public-home-hero" className="relative">
        <div className="grid gap-8 md:grid-cols-[1.1fr_0.9fr] md:items-center">
          <div>
            <p
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1 overline"
              style={{ color: "var(--color-primary)" }}
            >
              <Leaf size={12} weight="duotone" />
              Now welcoming families
            </p>
            <h1 className="mt-4 font-heading text-[2.25rem] font-bold leading-[1.05] tracking-tight text-neutral-900 sm:text-5xl lg:text-6xl">
              A warm, unhurried day for your little one.
            </h1>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-neutral-600 md:text-base">
              {homeHighlight
                ? renderPreview(homeHighlight.body_md, 240)
                : `${org.display_name || org.name} is a small, thoughtful childcare center where every child is known by name. We keep classrooms calm, outdoors time unrushed, and parents in the loop every day.`}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`${base}/contact`}
                data-testid="public-home-cta-contact"
                className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-105"
                style={{ background: "var(--color-primary)" }}
              >
                Schedule a visit
                <ArrowRight size={14} weight="bold" />
              </Link>
              {(pages || []).length > 0 && (
                <Link
                  to={homeHighlight ? `${base}/page/${homeHighlight.slug}` : `${base}/page/${pages[0].slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50"
                >
                  Learn more
                </Link>
              )}
              <ShareButton
                variant="ghost"
                className="sm:hidden"
                url={canonical}
                title={title}
                text={description}
              />
            </div>
          </div>

          <aside
            data-testid="public-home-card"
            className="relative overflow-hidden rounded-2xl border p-6 shadow-[0_20px_60px_-30px_rgba(20,22,26,0.25)]"
            style={{
              borderColor: "color-mix(in srgb, var(--color-primary) 30%, transparent)",
              background:
                "linear-gradient(150deg, color-mix(in srgb, var(--color-primary) 14%, #fff), #fff 60%, color-mix(in srgb, var(--color-accent) 12%, #fff))",
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <p
                  className="overline"
                  style={{ color: "var(--color-primary)" }}
                >
                  At a glance
                </p>
                <h3 className="mt-1 font-heading text-xl font-semibold text-neutral-900">
                  Quiet mornings · Big play
                </h3>
              </div>
              <Sparkle size={20} weight="duotone" className="text-neutral-400" />
            </div>
            <ul className="mt-5 space-y-3 text-sm text-neutral-700">
              <HighlightRow icon={Sun} label="Calm, predictable rhythm" />
              <HighlightRow icon={Leaf} label="Outdoor time every day" />
              <HighlightRow icon={CalendarBlank} label="Daily reports in the parent app" />
            </ul>
          </aside>
        </div>
      </section>

      {/* Module E · auto-detected programs section */}
      {programCards.length > 0 && (
        <section data-testid="public-home-programs" className="space-y-4">
          <div className="flex items-baseline justify-between">
            <div>
              <p
                className="overline"
                style={{ color: "var(--color-primary)" }}
              >
                Programs
              </p>
              <h2 className="mt-1 font-heading text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
                Made for each age
              </h2>
            </div>
            <Link
              to={`${base}/page/${programsPage.slug}`}
              data-testid="public-home-programs-all"
              className="text-sm font-semibold text-neutral-600 hover:text-neutral-900"
            >
              See all programs →
            </Link>
          </div>
          <div
            className={`grid gap-4 md:grid-cols-${Math.min(programCards.length, 3)}`}
            data-testid="public-home-programs-cards"
          >
            {programCards.map((card, idx) => (
              <Link
                key={idx}
                to={`${base}/page/${programsPage.slug}`}
                data-testid={`public-home-programs-card-${idx}`}
                className="group flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-5 transition hover:-translate-y-[2px] hover:shadow-md"
              >
                <p
                  className="overline"
                  style={{ color: "var(--color-primary)" }}
                >
                  Program
                </p>
                <h3 className="mt-1 font-heading text-lg font-semibold text-neutral-900">
                  {card.title}
                </h3>
                <p className="mt-2 line-clamp-3 flex-1 text-sm text-neutral-600">
                  {card.preview}
                </p>
                <span
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold transition group-hover:translate-x-0.5"
                  style={{ color: "var(--color-primary)" }}
                >
                  Learn more <ArrowRight size={11} weight="bold" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Announcements strip */}
      {visibleAnns.length > 0 && (
        <section data-testid="public-home-announcements" className="space-y-4">
          <div className="flex items-baseline justify-between">
            <div>
              <p
                className="overline"
                style={{ color: "var(--color-primary)" }}
              >
                Latest
              </p>
              <h2 className="mt-1 font-heading text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
                What's going on at {org.display_name || org.name}
              </h2>
            </div>
            {announcements.length > 3 && (
              <Link
                to={`${base}/announcements`}
                className="text-sm font-semibold text-neutral-600 hover:text-neutral-900"
              >
                All posts →
              </Link>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {visibleAnns.map((a) => (
              <article
                key={a.id}
                data-testid="public-announcement-card"
                className="flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-5 transition hover:-translate-y-[2px] hover:shadow-md"
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
                  {a.published_at ? new Date(a.published_at).toLocaleDateString() : ""}
                </p>
                <h3 className="mt-1 font-heading text-lg font-semibold text-neutral-900">
                  {a.title}
                </h3>
                <p className="mt-2 line-clamp-3 text-sm text-neutral-600">
                  {renderPreview(a.body_md, 160)}
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* CMS pages peek — only if there are pages NOT linked in the top nav */}
      {(pages || []).length > 0 && (
        <section data-testid="public-home-pages" className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
            Get to know us
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {(pages || []).slice(0, 4).map((p) => (
              <Link
                key={p.id}
                to={`${base}/page/${p.slug}`}
                data-testid="public-home-page-card"
                className="group flex items-start justify-between gap-4 rounded-2xl border border-neutral-200 bg-white p-6 transition hover:-translate-y-[2px] hover:shadow-md"
              >
                <div className="min-w-0">
                  <p className="font-heading text-lg font-semibold text-neutral-900">
                    {p.title}
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm text-neutral-600">
                    {renderPreview(p.body_md)}
                  </p>
                </div>
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="flex-shrink-0 text-neutral-400 transition group-hover:translate-x-0.5 group-hover:text-neutral-800"
                />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section
        data-testid="public-home-cta"
        className="overflow-hidden rounded-2xl border p-8 md:p-10"
        style={{
          borderColor: "color-mix(in srgb, var(--color-primary) 30%, transparent)",
          background:
            "linear-gradient(110deg, color-mix(in srgb, var(--color-primary) 10%, #fff) 0%, #fff 70%)",
        }}
      >
        <div className="grid items-center gap-6 md:grid-cols-[1.2fr_0.8fr]">
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
              Tell us a little about your family.
            </h2>
            <p className="mt-3 max-w-xl text-sm text-neutral-600 md:text-base">
              We'll get back to you within one business day. No forms to fill out in a waiting room —
              just a short note so we know who's coming.
            </p>
          </div>
          <div className="md:justify-self-end">
            <Link
              to={`${base}/contact`}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-105"
              style={{ background: "var(--color-primary)" }}
            >
              Start an inquiry
              <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Opt-in preview for parents */}
      <section className="rounded-2xl border border-neutral-200 bg-white p-6 text-center md:p-8">
        <p className="overline text-neutral-500">
          Already enrolled?
        </p>
        <p className="mt-2 max-w-xl font-heading text-lg font-semibold text-neutral-900 md:mx-auto">
          Check daily reports, messages, photos, and billing in the parent portal.
        </p>
        <div className="mt-4 flex justify-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-5 py-2 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50"
          >
            Go to parent portal
          </Link>
        </div>
      </section>
    </div>
  );
}

function HighlightRow({ icon: Icon, label }) {
  return (
    <li className="flex items-center gap-3">
      <span
        className="flex h-7 w-7 items-center justify-center rounded-xl"
        style={{
          background: "color-mix(in srgb, var(--color-primary) 14%, transparent)",
          color: "var(--color-primary)",
        }}
      >
        <Icon size={14} weight="duotone" />
      </span>
      <span>{label}</span>
    </li>
  );
}

function renderPreview(md, max = 180) {
  if (!md) return "";
  return String(md)
    // Normalize any literal backslash-n sequences (happens when the DB
    // body_md was written with JSON-escaped newlines).
    .replace(/\\n/g, " ")
    // Strip common markdown tokens so the card preview stays clean.
    .replace(/^#+\s*/gm, "")
    .replace(/[*_`>]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

/** Parse `## Heading` sections out of the Programs CMS page. The first
 *  paragraph after each heading is used as the card preview. Returns
 *  up to `limit` cards. Skips sections whose title contains "what" or
 *  "every" (those tend to be summary/footer sections). */
function parseProgramCards(md, limit = 3) {
  if (!md) return [];
  const lines = String(md).replace(/\\n/g, "\n").split(/\r?\n/);
  const sections = [];
  let cur = null;
  for (const raw of lines) {
    const line = raw.trim();
    const heading = line.match(/^##\s+(.+?)\s*$/);
    if (heading) {
      if (cur && cur.preview.trim()) sections.push(cur);
      cur = { title: heading[1].replace(/[#*_`]/g, "").trim(), preview: "" };
      continue;
    }
    if (cur && line && cur.preview.length < 200) {
      cur.preview += (cur.preview ? " " : "") + line.replace(/[*_`]/g, "");
    }
  }
  if (cur && cur.preview.trim()) sections.push(cur);
  return sections
    .filter((s) => !/\b(what|every|shared|included)\b/i.test(s.title))
    .slice(0, limit)
    .map((s) => ({ title: s.title, preview: s.preview.trim().slice(0, 180) }));
}
